import { Button, Stack, Text } from '@chakra-ui/react'
import type { SignInMode } from '@ipc/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '~/api/client'
import { keys } from '~/api/hooks'
import { Rich, useT } from '~/i18n/use-t'
import { CodeEntry, SIGN_IN_RESEND_MS as RESEND_MS } from './CodeEntry'
import { ErrorAlert, useFriendlyError } from './feedback'

/**
 * "Sign in again with a link from your email" (passwordless P2, P4): a first password, the data export and the
 * erasure need a sign-in of the last 10 minutes. The voter asks for the sign-in email here; the emailed code signs in
 * again on this page (the link does too, from the email). A `password_and_email` account gets no sign-in email
 * (P11): it signs out and signs in again with the password and the code.
 */
export function FreshSignIn({
  email,
  mode,
  intro,
  onSignedIn,
  onSignOut,
  signingOut = false,
  formId = 'fresh-code-form',
}: {
  email: string | null
  mode: SignInMode | undefined
  /** Why a new sign-in is needed, in the words of the page. */
  intro: string
  onSignedIn?: () => void
  /** Ends the session (every device) and opens the sign-in page: the path of a password + code account. */
  onSignOut?: () => void
  signingOut?: boolean
  formId?: string
}) {
  const { t } = useT()
  const friendly = useFriendlyError()
  const qc = useQueryClient()
  const [resendAt, setResendAt] = useState(0)
  const send = useMutation({
    mutationFn: () => api('memberSignInRequest', { body: { email: email ?? '' } }),
    onSuccess: () => setResendAt(Date.now() + RESEND_MS),
  })

  if (mode === 'password_and_email' || !email)
    return (
      <Stack gap={3} role='group' aria-label={t('freshSignIn.title')}>
        <Text>{intro}</Text>
        {mode === 'password_and_email' && <Text>{t('freshSignIn.passwordAndCode')}</Text>}
        {onSignOut && (
          <Button
            variant='outline'
            alignSelf={{ base: 'stretch', sm: 'flex-start' }}
            loading={signingOut}
            onClick={onSignOut}
          >
            {t('freshSignIn.signOutAndIn')}
          </Button>
        )}
      </Stack>
    )

  if (!send.isSuccess)
    return (
      <Stack gap={3} role='group' aria-label={t('freshSignIn.title')}>
        <Text>{intro}</Text>
        {send.isError && <ErrorAlert>{friendly(send.error)}</ErrorAlert>}
        <Button
          variant='outline'
          alignSelf={{ base: 'stretch', sm: 'flex-start' }}
          loading={send.isPending}
          onClick={() => send.mutate()}
        >
          {t('freshSignIn.send')}
        </Button>
      </Stack>
    )

  return (
    <Stack gap={3} role='group' aria-label={t('freshSignIn.title')}>
      <Text role='status'>
        <Rich k='freshSignIn.sent' values={{ email }} />
      </Text>
      <CodeEntry
        formId={formId}
        resendAt={resendAt}
        faq={null}
        copy={{
          submit: t('login.signIn'),
          resend: t('login.resendEmail'),
          resent: t('login.resentEmail'),
          expired: t('login.errCodeExpired'),
          tooMany: t('login.errCodeTooMany'),
          challenge: t('login.errChallenge'),
        }}
        onCheck={async (code) => {
          const me = await api('memberSignInCode', { body: { code } })
          qc.setQueryData(keys.me, me)
          await qc.invalidateQueries({ queryKey: keys.signIn })
          send.reset()
          onSignedIn?.()
        }}
        onResend={async () => {
          await api('memberSignInRequest', { body: { email } })
          setResendAt(Date.now() + RESEND_MS)
        }}
      />
    </Stack>
  )
}
